import { Component, inject, signal, ElementRef, effect, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LayoutService } from '../../commons/layout-service';
import { CartService } from '../../commons/cart-service';
import { OrderTrackingService } from '../../commons/order-tracking.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { TrackingJwtService } from '../../commons/tracking-jwt-service';
import { filter, map, startWith } from 'rxjs';

const isTableQrRoute = (url: string): boolean =>
  /^\/table\/\d+(\/|$)/.test(url.split('?')[0].split('#')[0]);

const parseTableNoFromUrl = (url: string): number | null => {
  const match = /^\/table\/(\d+)(?:\/|$)/.exec(url.split('?')[0].split('#')[0]);
  if (!match) {
    return null;
  }
  const tableNo = Number(match[1]);
  return Number.isFinite(tableNo) && tableNo > 0 ? tableNo : null;
};

@Component({
  selector: 'app-header-component',
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
],
  templateUrl: './header-component.html',
  styleUrl: './header-component.scss',
})
export class HeaderComponent {
  private host = inject(ElementRef).nativeElement as HTMLElement;
  private router = inject(Router);
  layout = inject(LayoutService);
  cartService = inject(CartService);
  orderTracking = inject(OrderTrackingService);
  trackingJwtService = inject(TrackingJwtService);
  mobileMenuOpen = signal(false);
  /** QR table landing: hide delivery / takeaway / booking nav — guest orders at this table only. */
  tableQrContext = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => isTableQrRoute(this.router.url)),
    ),
    { initialValue: isTableQrRoute(inject(Router).url) },
  );
  private _docClick = (e: Event) => this._handleDocClick(e);

  constructor() {
    const trackingJwt = this.trackingJwtService.readPersistedTrackingJwt();
    if (trackingJwt) {
      this.orderTracking.ensureOrderStream(trackingJwt);
    }

    // when mobileMenuOpen toggles, attach/remove outside click listener (browser only)
    effect(() => {
      if (typeof document === 'undefined') return;
      if (this.mobileMenuOpen()) {
        document.addEventListener('click', this._docClick, true);
      } else {
        document.removeEventListener('click', this._docClick, true);
      }
    });
  }
  
  onToggleMobileMenu() {
    this.mobileMenuOpen.set(!this.mobileMenuOpen());
  }

  private _handleDocClick(e: Event) {
    const target = e.composedPath ? e.composedPath()[0] as Node : e.target as Node;
    if (!this.host) return;
    if (!this.host.contains(target)) {
      this.mobileMenuOpen.set(false);
    }
  }

  ngOnDestroy() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this._docClick, true);
    }
  }

  hasTrackingJwt(): boolean {
    return !!this.trackingJwtService.readPersistedTrackingJwt();
  }
  
  navigateToLanding(): void {
    this.mobileMenuOpen.set(false);
    const tableFromUrl = parseTableNoFromUrl(this.router.url);
    const tableNo = tableFromUrl ?? this.cartService.tableNoSignal();
    if (tableNo > 0) {
      void this.router.navigate(['/table', tableNo]);
      return;
    }
    void this.router.navigate(['/']);
  }

  navigateToAboutUs() {
    this.mobileMenuOpen.set(false);
    this.router.navigate(['/about']);
  }

  navigateToWorkWithUs() {
    this.mobileMenuOpen.set(false);
    this.router.navigate(['/work-with-us']);
  }

  navigateToMyOrders() {
    this.router.navigate(['/my-orders']);
  }

  navigateToOrderFoodAtTable() {
    this.cartService.selectedOrderType.set('Dine-In');
    this.router.navigate(['/order'], { queryParams: { type: 'at-table' } });
  }

  navigateToBookATable() {
    this.router.navigate(['/book-a-table']);
  }

  navigateToOrderForTakeaway() {
    this.cartService.selectedOrderType.set('Takeaway');
    this.router.navigate(['/order'], { queryParams: { type: 'take-away' } });
  }

  navigateToOrderForDelivery() {
    this.cartService.selectedOrderType.set('Delivery');
    this.router.navigate(['/order'], { queryParams: { type: 'delivery' } });
  }

  onOrderNotifClick() {
    this.orderTracking.clearNotification();
  }

  onOrderNotifMenuItemClick() {
    this.orderTracking.clearNotification();
    this.navigateToMyOrders();
  }

  orderNotifSummary(): string {
    const n = this.orderTracking.latestNotification();
    if (!n) {
      return 'You’re connected for live order and reservation updates on this visit.';
    }
    if (n.kind === 'refund') {
      const amount =
        n.amountInr != null ? `₹${n.amountInr}` : 'Your payment';
      const state = n.refundState?.toUpperCase();
      if (state === 'COMPLETED') {
        return `${amount} refund has been credited to your payment method.`;
      }
      if (state === 'FAILED') {
        return `We could not complete the ${amount} refund automatically. Check My Orders for details.`;
      }
      return `${amount} refund is being processed — usually within 5–7 business days.`;
    }
    if (n.kind === 'order') {
      return `Your order status is now: ${n.status}`;
    }
    return `Your reservation status is now: ${n.status}`;
  }
}
